import { useState, useMemo, useContext } from "react";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useAuth0 } from "@auth0/auth0-react";
import DataTable from "cxfcomponents/Tables/DataTable";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import TextWithEllipsis from "layouts/pages/logs/components/TextWithEllipsis";
import StatusBadge from "cxfcomponents/StatusBadge";
import UsersCell from "layouts/Flows/components/UsersCell";
import { useUserFlows } from "hooks/useUserFlows";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { useSelector } from "react-redux";
import defaultFlowIcon from "assets/images/for-flow/flow-icon-list-view.svg";
import { useNavigate } from "react-router-dom";
import { useResourceListener } from "hooks/useResourceListener";
import { useResourceDependencies } from "hooks/useResourceDependencies";

export const Flows = ({ connector }) => {
   const [searchName, setSearchName] = useState("");
   const { flows, handleFetchFlows } = usePortalFlows();
   const { userFlows } = useUserFlows();
   const { user } = useAuth0();
   const { users } = useSelector((store) => store?.portal?.users);
   const { userPermissions } = useContext(PermissionsContext);
   const navigate = useNavigate();
   const { dependencies: connectorFlowIds } = useResourceDependencies({
      sourceId: connector?.id,
      direction: "reverse",
      targetResourceType: "flow"
   });
   useResourceListener("flowsSharingUpdate", handleFetchFlows);

   const getStatus = (flow) => {
      if (flow.trashed) return "deleted";
      if (flow.shared_status === "test") return "test";
      if (flow.steps === "") return "draft";
      return "active";
   };

   const filterFlowBySearchName = (flow) => {
      if (!searchName) return true;

      return flow.name.toLowerCase().includes(searchName);
   };

   const tableData = useMemo(() => {
      const filteredFlows = flows
         .filter((flow) => connector?.id && connectorFlowIds.includes(flow.id))
         .filter(filterFlowBySearchName);
      return {
         columns: [
            {
               Header: "flowId",
               accessor: "flowId",
               show: false
            },
            {
               Header: "icon",
               accessor: "icon",
               canGroupBy: false,
               disableSortBy: true,
               Cell: ({ value }) => (
                  <SoftBox width="60px">
                     <SoftBox
                        component="img"
                        width="100%"
                        minHeight="60px"
                        border="0.5px solid #E6E6E6"
                        borderRadius="12px"
                        sx={{
                           objectFit: "cover"
                        }}
                        src={value}
                     />
                  </SoftBox>
               )
            },
            {
               Header: "name",
               accessor: "nameWithBadge",
               width: 250,
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftBox
                     className="white-to-dark"
                     fontSize="14px"
                     fontWeight="bold"
                     display="flex"
                     alignItems="center"
                     sx={{
                        minWidth: "150px",
                        maxWidth: "200px",
                        whiteSpace: "normal",
                        cursor: "pointer",
                        wordBreak: "break-all"
                     }}
                     onClick={() => navigate(`/flow/${value?.id}`)}
                  >
                     {value?.name?.length > 24 ? value?.name?.slice(0, 24) + "..." : value?.name}
                  </SoftBox>
               ),
               sortType: (rowA, rowB, columnId) => {
                  const nameA = rowA.values[columnId].name;
                  const nameB = rowB.values[columnId].name;
                  return nameA.localeCompare(nameB);
               }
            },
            {
               Header: "description",
               accessor: "description",
               canGroupBy: false,
               Cell: ({ value }) => {
                  return (
                     <SoftBox>
                        <TextWithEllipsis value={{ name: value, maxWidth: "clamp(100px, 20vw, 300px)" }} />
                     </SoftBox>
                  );
               }
            },
            {
               Header: "name",
               accessor: "name",
               canGroupBy: false,
               show: false
            },
            {
               Header: "status",
               accessor: "status",
               canGroupBy: false,
               Cell: ({ value }) => {
                  return <StatusBadge status={value} />;
               }
            },
            {
               Header: "shared status",
               accessor: "sharedStatus",
               canGroupBy: true,
               expanded: true
            },
            {
               Header: "users",
               accessor: "users",
               canGroupBy: false,
               Cell: ({ value }) => {
                  return <UsersCell users={value} />;
               },
               disableSortBy: true
            },
            {
               Header: "last edited",
               accessor: "lastEdited",
               width: 170,
               canGroupBy: false,
               disableSortBy: false,
               defaultSortOrder: "desc",
               sortType: (rowA, rowB, columnId) => {
                  const dateA = new Date(rowA.values[columnId]);
                  const dateB = new Date(rowB.values[columnId]);
                  return dateA - dateB;
               }
            }
         ].map((column) => ({ ...column, disableResize: true })),
         rows: filteredFlows.map((item) => {
            const flowOwnerId = item.created_by_id;

            const flowOwner = users?.filter((user) => flowOwnerId.includes(user.auth0_id)) || [];

            const filteredUsers = userFlows?.filter((flow) => flow.flow_id === item.id);

            const sharedToUsers = filteredUsers?.map((user) => {
               return user.user_id;
            });

            const sharedUsers = users?.filter((user) => sharedToUsers.includes(user.auth0_id)) || [];

            return {
               flowId: item.id,
               nameWithBadge: { trashed: item.trashed, name: item.name, id: item.id },
               name: item.name,
               sharedStatus: item.created_by_id === user?.cxfUserId ? "Owner" : "",
               status: `${getStatus(item)}`,
               users: [...flowOwner, ...sharedUsers],
               lastEdited: formatDateAndTime(getLocalDateTimeString(item.updated_date_time || item.created_date_time)) || "",
               description: item?.description,
               icon: item.logo ? item.logo : defaultFlowIcon
            };
         })
      };
   }, [
      flows,
      userFlows,
      users,
      userPermissions,
      searchName,
      connectorFlowIds,
      connector?.id,
      user?.cxfUserId
   ]);

   return (
      <>
         <SoftBox>
            <SoftBox
               sx={{
                  alignItems: "flex-start",
                  display: "flex",
                  justifyContent: "flex-start",
                  padding: "12px 0 12px 40px"
               }}
            >
               <SoftInput
                  size="medium"
                  placeholder="Search..."
                  value={searchName}
                  onChange={({ currentTarget }) => {
                     setSearchName(currentTarget.value);
                  }}
                  sx={{
                     maxWidth: "380px",
                     marginRight: "34px"
                  }}
               />
            </SoftBox>
            <DataTable table={tableData} entriesPerPage={{ defaultValue: 25 }} hiddenTopButtons tableView="list" noTopPart tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))" />
         </SoftBox>
      </>
   );
};
